import React, { useEffect, useState } from 'react';
import AppLayout from '../../components/layout/AppLayout';
import { PageHeader, EmptyState } from '../../components/common/PageHeader';
import api from '../../lib/api';
import { UserCheck, Check, X, Clock, Mail, Phone, Languages, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const AdminApprovals = () => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/pending');
      setPending(res.data);
    } catch (err) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const approve = async (userId) => {
    setProcessing(p => ({ ...p, [userId]: 'approve' }));
    try {
      await api.post(`/admin/approve/${userId}`);
      toast.success('Konto freigeschaltet');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler');
    } finally {
      setProcessing(p => ({ ...p, [userId]: null }));
    }
  };

  const reject = async (userId) => {
    if (!window.confirm('Konto wirklich ablehnen?')) return;
    setProcessing(p => ({ ...p, [userId]: 'reject' }));
    try {
      await api.post(`/admin/reject/${userId}`);
      toast.success('Konto abgelehnt');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler');
    } finally {
      setProcessing(p => ({ ...p, [userId]: null }));
    }
  };

  return (
    <AppLayout>
      <PageHeader
        title="Freischaltungen"
        description="Neue Dolmetscher und Kunden überprüfen und freischalten"
      >
        <div className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-800 px-3 py-1.5 rounded-md text-xs font-medium" data-testid="pending-count">
          <Clock className="w-3.5 h-3.5" /> {pending.length} offen
        </div>
      </PageHeader>

      {loading ? (
        <div className="text-center py-12 text-slate-500">Lade...</div>
      ) : pending.length === 0 ? (
        <EmptyState
          icon={UserCheck}
          title="Keine offenen Freischaltungen"
          description="Alle Dolmetscher und Kunden sind bereits freigeschaltet."
        />
      ) : (
        <div className="space-y-4">
          {pending.map((u) => {
            const profile = u.profile || {};
            const isInterpreter = u.role === 'interpreter';
            return (
              <div
                key={u.user_id}
                className="bg-white border border-slate-200 rounded-md card-subtle p-6"
                data-testid={`pending-${u.user_id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-[#0F4C81]/10 flex items-center justify-center text-[#0F4C81] font-heading font-semibold">
                        {(u.name || u.email).charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-heading font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                          <span>
                            {isInterpreter && profile.first_name
                              ? `${profile.salutation ? profile.salutation + ' ' : ''}${profile.first_name} ${profile.last_name}`
                              : u.name || u.email}
                          </span>
                          {isInterpreter && profile.sworn && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-[#0F4C81] text-white rounded px-1.5 py-0.5" title="Allgemein beeidigt / vereidigt">
                              <ShieldCheck className="w-3 h-3" /> Vereidigt
                            </span>
                          )}
                        </h3>
                        <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5">
                          <span className="inline-flex items-center gap-1">
                            <Mail className="w-3 h-3" /> {u.email}
                          </span>
                          {profile.phone && (
                            <span className="inline-flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {profile.phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                        {isInterpreter ? 'Dolmetscher' : 'Kunde'}
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                        {u.status === 'pending' ? 'Wartet auf Freischaltung' : 'Inaktiv'}
                      </span>
                      <span className="text-xs text-slate-400">
                        Registriert: {new Date(u.created_at).toLocaleDateString('de-DE')}
                      </span>
                    </div>

                    {isInterpreter && (
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-slate-700 mt-3 pt-3 border-t border-slate-100">
                        <div className="flex items-start gap-1.5">
                          <Languages className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <span className="text-slate-500">Sprachen:</span>{' '}
                            {(profile.languages || []).join(', ') || '-'} →{' '}
                            {(profile.target_languages || []).join(', ') || '-'}
                          </div>
                        </div>
                        <div>
                          <span className="text-slate-500">Fachgebiete:</span>{' '}
                          {(profile.specializations || []).join(', ') || '-'}
                        </div>
                        <div>
                          <span className="text-slate-500">Einsatzarten:</span>{' '}
                          {(profile.service_types || []).join(', ') || '-'}
                        </div>
                        {profile.address && (
                          <div>
                            <span className="text-slate-500">Adresse:</span> {profile.address}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <button
                      onClick={() => approve(u.user_id)}
                      disabled={!!processing[u.user_id]}
                      data-testid={`approve-${u.user_id}`}
                      className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-md px-4 py-2 text-sm disabled:opacity-50"
                    >
                      <Check className="w-4 h-4" />
                      {processing[u.user_id] === 'approve' ? 'Lade...' : 'Freischalten'}
                    </button>
                    <button
                      onClick={() => reject(u.user_id)}
                      disabled={!!processing[u.user_id]}
                      data-testid={`reject-${u.user_id}`}
                      className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-medium rounded-md px-4 py-2 text-sm disabled:opacity-50"
                    >
                      <X className="w-4 h-4" />
                      Ablehnen
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AppLayout>
  );
};

export default AdminApprovals;
